import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import createApiClient from "../../utils/createApiClient";

export interface AddWhiteLabelData {
  brandLogo: string;
  brandColor: string;
  textColor: string;
  bgColor: string;
  domainURL: string;
  termsOfServiceURL: string;
  privacyPolicyURL: string;
}

export interface AddWhiteLabelResponse {
  _id: string;
  userId: {
    id: string;
    firstname: string;
    lastname: string;
    email: string;
  };
  brandLogo: string;
  brandColor: string;
  textColor: string;
  bgColor: string;
  domainURL: string;
  termsOfServiceURL: string;
  privacyPolicyURL: string;
  createdAt: string;
  updatedAt: string;
}

export interface AddWhiteLabelApiResponse {
  success: boolean;
  message: string;
  data?: {
    whiteLabel: AddWhiteLabelResponse;
  };
}

export interface AddWhiteLabelsApiResponse {
  success: boolean;
  message: string;
  data?: {
    whiteLabels: AddWhiteLabelResponse[];
    total: number;
    page: number;
    pages: number;
  };
}

function isAxiosError(error: unknown): error is import("axios").AxiosError {
  return (error as import("axios").AxiosError).isAxiosError !== undefined;
}

// CREATE WHITE LABEL ACTION
export const createAddWhiteLabel = createAsyncThunk<
  AddWhiteLabelApiResponse,
  AddWhiteLabelData,
  { rejectValue: string }
>(
  "addWhiteLabel/createAddWhiteLabel",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await createApiClient.post<AddWhiteLabelApiResponse>(
        "/add-white-label",
        formData
      );

      if (response.data.success) {
        toast.success(
          response.data.message ||
            "White label configuration created successfully"
        );
        return response.data;
      } else {
        toast.error(
          response.data.message || "Failed to create white label configuration"
        );
        return rejectWithValue(
          response.data.message || "Failed to create white label configuration"
        );
      }
    } catch (error) {
      let errorMessage = "An unknown error occurred";
      if (isAxiosError(error) && error.response) {
        errorMessage =
          (error.response.data as AddWhiteLabelApiResponse).message ||
          "Unknown error";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// GET ALL WHITE LABELS ACTION
export const getAllAddWhiteLabels = createAsyncThunk<
  AddWhiteLabelsApiResponse,
  { page?: number; limit?: number },
  { rejectValue: string }
>(
  "addWhiteLabel/getAllAddWhiteLabels",
  async ({ page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await createApiClient.get<AddWhiteLabelsApiResponse>(
        `/add-white-label?page=${page}&limit=${limit}`
      );

      if (response.data.success) {
        return response.data;
      } else {
        toast.error(
          response.data.message ||
            "Failed to retrieve white label configurations"
        );
        return rejectWithValue(
          response.data.message ||
            "Failed to retrieve white label configurations"
        );
      }
    } catch (error) {
      let errorMessage = "An unknown error occurred";
      if (isAxiosError(error) && error.response) {
        errorMessage =
          (error.response.data as AddWhiteLabelsApiResponse).message ||
          "Unknown error";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// GET MY WHITE LABEL ACTION
export const getMyAddWhiteLabel = createAsyncThunk<
  AddWhiteLabelApiResponse,
  void,
  { rejectValue: string }
>("addWhiteLabel/getMyAddWhiteLabel", async (_, { rejectWithValue }) => {
  try {
    const response = await createApiClient.get<AddWhiteLabelApiResponse>(
      "/add-white-label/me"
    );

    if (response.data.success) {
      return response.data;
    } else {
      toast.error(
        response.data.message || "Failed to retrieve white label configuration"
      );
      return rejectWithValue(
        response.data.message || "Failed to retrieve white label configuration"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as AddWhiteLabelApiResponse).message ||
        "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

// UPDATE MY WHITE LABEL ACTION
export const updateMyAddWhiteLabel = createAsyncThunk<
  AddWhiteLabelApiResponse,
  Partial<AddWhiteLabelData>,
  { rejectValue: string }
>("addWhiteLabel/updateMyAddWhiteLabel", async (data, { rejectWithValue }) => {
  try {
    const response = await createApiClient.put<AddWhiteLabelApiResponse>(
      "/add-white-label/me",
      data
    );

    if (response.data.success) {
      toast.success(
        response.data.message ||
          "White label configuration updated successfully"
      );
      return response.data;
    } else {
      toast.error(
        response.data.message || "Failed to update white label configuration"
      );
      return rejectWithValue(
        response.data.message || "Failed to update white label configuration"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as AddWhiteLabelApiResponse).message ||
        "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

// DELETE MY WHITE LABEL ACTION
export const deleteMyAddWhiteLabel = createAsyncThunk<
  AddWhiteLabelApiResponse,
  void,
  { rejectValue: string }
>("addWhiteLabel/deleteMyAddWhiteLabel", async (_, { rejectWithValue }) => {
  try {
    const response = await createApiClient.delete<AddWhiteLabelApiResponse>(
      "/add-white-label/me"
    );

    if (response.data.success) {
      toast.success(
        response.data.message ||
          "White label configuration deleted successfully"
      );
      return response.data;
    } else {
      toast.error(
        response.data.message || "Failed to delete white label configuration"
      );
      return rejectWithValue(
        response.data.message || "Failed to delete white label configuration"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as AddWhiteLabelApiResponse).message ||
        "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});
